import React, { useState } from "react";
import { InMeetingService } from "../../services/inMeeting.service";
import { toast } from "react-toastify";
import ScheduleForm from "./ScheduleForm";
import { CancelRescheduleForm } from "./CancelRescheduleForm";

type Payload = {
  Candidate: {
    CandidateID: string;
  };
  Interview: {
    StartDateTime: string;
    EndDateTime: string;
  };
  JobRequisition: {
    JobRequisitionID: string;
  };
  EI: {
    EIEnterpriseID: string;
  }[];
};

const CandidateForm: React.FC = () => {
  const meetingService = new InMeetingService();

  const initialFormData: Payload = {
    Candidate: { CandidateID: "" },
    Interview: { StartDateTime: "", EndDateTime: "" },
    JobRequisition: { JobRequisitionID: "" },
    EI: [{ EIEnterpriseID: "" }],
  };

  const [submitLoading, setSubmitLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [candidateID, setCandidateID] = useState("");
  const [meetingData, setMeetingData] = useState<any>({});
  const [tab, setTab] = useState(1);
  const [formData, setFormData] = useState<Payload>(initialFormData);

  // Track which meeting is currently editable
  const [editingIndex, setEditingIndex] = useState<number | null>(null);

  const showToast = (
    message: string,
    type: "success" | "error" | "info" = "info"
  ) => {
    toast(message, {
      position: "top-right",
      autoClose: 3000,
      hideProgressBar: true,
      pauseOnHover: false,
      draggable: false,
      type,
    });
  };

  const resetForm = () => {
    setFormData(initialFormData);
    setMeetingData({});
    setCandidateID("");
    setEditingIndex(null);
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    section: keyof Payload,
    key: string,
    index?: number
  ) => {
    let value = e.target.value;
    if (key.toLowerCase().includes("datetime") && value) {
      value = `${value}:00.000Z`;
    }

    setFormData((prev) => {
      if (section === "EI" && index !== undefined) {
        const updatedEI = [...prev.EI];
        (updatedEI[index] as any)[key] = value;
        return { ...prev, EI: updatedEI };
      }
      return {
        ...prev,
        [section]: {
          ...(prev[section] as any),
          [key]: value,
        },
      };
    });
  };

  const handleInputChange = (idx: number, field: string, value: string) => {
    setMeetingData((prev: any) => {
      const updatedDetails = [...prev.details];
      updatedDetails[idx] = { ...updatedDetails[idx], [field]: value };
      return { ...prev, details: updatedDetails };
    });
  };

  const onCancelReschedule = async (payload: any) => {
    setActionLoading(true);
    try {
      const res = await meetingService.CancelRescheduleMeeting(payload);
      console.log(res);
      if (res || res.data) {
        showToast(
          res?.data?.message || res?.message,
          res?.data?.status === "Failed" || res?.status === "Failed"
            ? "error"
            : "success"
        );
      }
    } catch (err) {
      console.error(err);
      showToast("Action failed", "error");
    } finally {
      handleSearch();
      setActionLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitLoading(true);
    const start = new Date(formData.Interview.StartDateTime).getTime();
    const end = new Date(formData.Interview.EndDateTime).getTime();
    const diffMinutes = (end - start) / (1000 * 60);
    const validIntervals = [15, 30, 45, 60, 120];
    const candidateId = formData?.Candidate?.CandidateID?.trim();
    const candidateIdPattern = /^C\d{8}$/;
    try {
      if (
        !candidateIdPattern.test(candidateId) &&
        import.meta.env.MODE !== "stage" &&
        import.meta.env.MODE !== "development"
      ) {
        showToast("Please enter a valid CID", "error");
        return;
      }
      if (isNaN(start) || isNaN(end)) {
        showToast("Start and End time must be valid.", "error");
        return;
      }
      if (start === end) {
        showToast("Start and End time cannot be the same.", "error");
        return;
      }
      if (!validIntervals.includes(diffMinutes)) {
        showToast(
          "End time must be 15, 30, 45, 60, or 120 minutes after Start time.",
          "error"
        );
        return;
      }
      const res = await meetingService.CreateMeeting(formData);
      console.log("resssssss", res);
      if (
        (res && res.status === 500) ||
        res.status === 400 ||
        res.status === 401
      ) {
        showToast(res.data.error.description, "error");
        return;
      }
      if (res && res.data !== null) {
        setFormData(initialFormData);
        showToast("Meeting scheduled successfully!", "success");
      } else if (res.error) {
        showToast(res.error.description, "error");
      } else {
        showToast("Form Submission failed, please try again!", "error");
      }
    } catch (err) {
      console.error(err);
      showToast("Form Submission failed, please try again!", "error");
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleSearch = async () => {
    setSearchLoading(true);
    setMeetingData({});
    meetingService
      .FetchCandidateMeetings({ CandidateId: candidateID })
      .then((res: any) => {
        setHasSearched(true);
        if (res && res.status) {
          showToast("Error fetching candidate details", "error");
        } else {
          setMeetingData(res || {});
        }
      })
      .catch(() => showToast("Error fetching candidate details", "error"))
      .finally(() => setSearchLoading(false));
  };

  const getMinDateTime = (): string => {
    const dt = new Date();
    return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(
      2,
      "0"
    )}-${String(dt.getDate()).padStart(2, "0")}T${String(
      dt.getHours()
    ).padStart(2, "0")}:${String(dt.getMinutes()).padStart(2, "0")}`;
  };

  type TabProps = {
    label: string;
    active: boolean;
    onClick: () => void;
  };

  const Tab: React.FC<TabProps> = ({ label, active, onClick }) => {
    const baseStyle: React.CSSProperties = {
      flex: 1,
      margin: 0,
      textAlign: "center",
      cursor: "pointer",
      padding: 24,
      transition: "background 0.2s, color 0.2s",
    };

    const activeStyle: React.CSSProperties = !active
      ? { background: "lightgrey", color: "#57585b" }
      : { background: "white", color: "#2563eb" };

    return (
      <h3 style={{ ...baseStyle, ...activeStyle }} onClick={onClick}>
        {label}
      </h3>
    );
  };

  const toLocalISOString = (date: Date): string => {
    const pad = (n: number) => n.toString().padStart(2, "0");

    const year = date.getFullYear();
    const month = pad(date.getMonth() + 1);
    const day = pad(date.getDate());
    const hours = pad(date.getHours());
    const minutes = pad(date.getMinutes());
    const seconds = pad(date.getSeconds());

    return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}.000Z`;
  };

  return (
    <form
      onSubmit={handleSubmit}
      style={{
        margin: "0 auto",
        backgroundColor: "#fff",
        fontFamily: "Arial, sans-serif",
        maxWidth: "550px",
        borderRadius: "10px",
        overflow: "hidden",
        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
      }}
    >
      <div style={{ display: "flex" }}>
        <Tab
          label="Schedule"
          active={tab === 1}
          onClick={() => {
            resetForm();
            setTab(1);
          }}
        />
        <Tab
          label="Re-schedule/Cancel"
          active={tab === 2}
          onClick={() => {
            resetForm();
            setTab(2);
          }}
        />
      </div>

      {tab === 1 ? (
        <ScheduleForm
          formData={formData}
          handleChange={handleChange}
          submitLoading={submitLoading}
          getMinDateTime={getMinDateTime}
        />
      ) : (
        <CancelRescheduleForm
          candidateID={candidateID}
          handleSearch={handleSearch}
          searchLoading={searchLoading}
          setCandidateID={(value) => {
            setHasSearched(false);
            setCandidateID(value);
          }}
        />
      )}

      {tab === 2 &&
        candidateID &&
        hasSearched &&
        !searchLoading &&
        !meetingData?.hasRecords && (
          <h4 style={{ textAlign: "center" }}>
            No Meeting Scheduled for this Canididate
          </h4>
        )}

      {tab === 2 &&
        meetingData?.details &&
        (() => {
          const visibleMeetings = meetingData.details.filter(
            (meeting: any) => !meeting.isCancel && !meeting.isFeedbackSubmitted
          );

          if (visibleMeetings.length === 0 && meetingData.details.length > 0) {
            return (
              <h4 style={{ textAlign: "center" }}>
                No Meeting Scheduled for this Candidate
              </h4>
            );
          }

          return (
            <div
              style={{ display: "grid", gap: "20px", padding: "0 24px 24px" }}
            >
              {visibleMeetings.map((meeting: any, idx: any) => {
                const handleAction = (action: "cancel" | "reschedule") => {
                  if (action === "reschedule") {
                    if (editingIndex === idx) {
                      const payload = {
                        candidateId: meeting.candidateID,
                        interviewerUID: meeting.interviewUID,
                        startDate: toLocalISOString(
                          new Date(meeting.startDateTime)
                        ),
                        endDate: toLocalISOString(
                          new Date(meeting.endDateTime)
                        ),
                        eventId: meeting.eventID,
                        isCancel: false,
                        isReschedule: true,
                      };
                      onCancelReschedule(payload);
                      setEditingIndex(null);
                    } else {
                      setEditingIndex(idx); // enable editing
                    }
                  } else {
                    const payload = {
                      candidateId: meeting.candidateID,
                      interviewerUID: meeting.interviewUID,
                      startDate: toLocalISOString(
                        new Date(meeting.startDateTime)
                      ),
                      endDate: toLocalISOString(new Date(meeting.endDateTime)),
                      eventId: meeting.eventID,
                      isCancel: true,
                      isReschedule: false,
                    };
                    onCancelReschedule(payload);
                  }
                };

                return (
                  <div
                    key={meeting.interviewUID}
                    style={{
                      border: "1px solid #ddd",
                      borderRadius: "10px",
                      padding: "16px",
                      boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
                    }}
                  >
                    <h3 style={{ marginTop: 0, color: "#2563eb" }}>
                      {meeting.candidateID}
                    </h3>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "12px",
                      }}
                    >
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns: "1fr 1fr",
                          gap: 10,
                        }}
                      >
                        <div>
                          <label
                            style={{
                              fontWeight: 600,
                              display: "block",
                              marginBottom: 5,
                            }}
                          >
                            Start Date & Time
                          </label>
                          <input
                            type="datetime-local"
                            value={meeting.startDateTime.slice(0, 16)}
                            min={getMinDateTime()}
                            onChange={(e) =>
                              handleInputChange(
                                idx,
                                "startDateTime",
                                e.target.value
                              )
                            }
                            disabled={editingIndex !== idx}
                            style={{
                              width: "100%",
                              padding: "8px",
                              borderRadius: "6px",
                              border: "1px solid #ccc",
                            }}
                          />
                        </div>
                        <div>
                          <label
                            style={{
                              fontWeight: 600,
                              display: "block",
                              marginBottom: 5,
                            }}
                          >
                            End Date & Time
                          </label>
                          <input
                            type="datetime-local"
                            value={meeting.endDateTime.slice(0, 16)}
                            onChange={(e) =>
                              handleInputChange(
                                idx,
                                "endDateTime",
                                e.target.value
                              )
                            }
                            min={getMinDateTime()}
                            disabled={editingIndex !== idx}
                            style={{
                              width: "100%",
                              padding: "8px",
                              borderRadius: "6px",
                              border: "1px solid #ccc",
                            }}
                          />
                        </div>
                      </div>
                      <div
                        style={{
                          display: "flex",
                          gap: "10px",
                          marginTop: "12px",
                        }}
                      >
                        <button
                          type="button"
                          onClick={() => handleAction("reschedule")}
                          disabled={actionLoading}
                          style={{
                            flex: 1,
                            padding: "10px",
                            backgroundColor: "#2563eb",
                            color: "#fff",
                            border: "none",
                            borderRadius: "6px",
                            cursor: actionLoading ? "not-allowed" : "pointer",
                          }}
                        >
                          {editingIndex === idx ? "Save" : "Reschedule"}
                        </button>
                        <button
                          type="button"
                          onClick={() => handleAction("cancel")}
                          disabled={actionLoading}
                          style={{
                            flex: 1,
                            padding: "10px",
                            backgroundColor: "#dc2626",
                            color: "#fff",
                            border: "none",
                            borderRadius: "6px",
                            cursor: actionLoading ? "not-allowed" : "pointer",
                          }}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })()}
    </form>
  );
};

export default CandidateForm;
